import { InjectionToken } from '@angular/core';
import { isDefined, isNil } from '@trimble-gcs/common';

export interface Selectable {
  id: string;
  selected: boolean;
}

export const TOGGLE_SELECTED = new InjectionToken<
  <T extends Selectable>(list: T[], item: T, event: MouseEvent, lastSelected?: T) => T[]
>('ToggleSelected', {
  factory: () => {
    return <T extends Selectable>(list: T[], item: T, event: MouseEvent, lastSelected?: T): T[] => {
      if (event.shiftKey) {
        const lastSelectedId = lastSelected?.id ?? list.at(0)?.id;
        return isNil(lastSelectedId) ? [] : toggleRangeSelected(list, item, lastSelectedId);
      }

      const checkboxClicked = isEventFromModusCheckbox(event);
      if (event.ctrlKey || checkboxClicked) return toggleSelect(item);

      return toggleSingleSelected(list, item);
    };
  },
});

function toggleSelect<T extends Selectable>(item: T): T[] {
  item.selected = !item.selected;
  return [item];
}

function toggleRangeSelected<T extends Selectable>(
  list: T[],
  item: T,
  lastSelectedId: string,
): T[] {
  const selectedIndex = list.findIndex((listItem) => listItem.id === item.id);
  const prevSelectedIndex = list.findIndex((listItem) => listItem.id === lastSelectedId);

  const startIndex = selectedIndex < prevSelectedIndex ? selectedIndex : prevSelectedIndex;
  const endIndex = selectedIndex > prevSelectedIndex ? selectedIndex : prevSelectedIndex;

  const selection = list.filter((_, index) => index >= startIndex && index <= endIndex);
  if (selectedIndex < prevSelectedIndex) selection.reverse();

  const selected = !item.selected;
  const changedItems = selection
    .filter((item) => item.selected != selected)
    .map((item) => ({ ...item, selected }));

  return changedItems;
}

function toggleSingleSelected<T extends Selectable>(list: T[], item: T) {
  const currentSelection = list.filter((item) => item.selected);
  const inCurrentSelection = currentSelection.find((listItem) => listItem.id === item.id);

  currentSelection.forEach((item) => (item.selected = false));

  //unselect scan if it's the only current selection
  item.selected = currentSelection.length === 1 && inCurrentSelection ? false : true;

  if (!inCurrentSelection) currentSelection.push(item);

  return currentSelection;
}

function isEventFromModusCheckbox(value: Event): boolean {
  const src = value.target;
  return isDefined(src) && 'tagName' in src && src.tagName === 'MODUS-CHECKBOX';
}
